/**
 * todayV2Store — estado runtime de la vista previa "Hoy V2".
 *
 * Mismo patrón que `pmsRuntimeStore` y `settingsV2Store`: un módulo,
 * `useSyncExternalStore`, cero persistencia. Al recargar la página vuelve el
 * dataset semilla; no se usa localStorage porque la demo PMS tampoco lo hace
 * y persistir daría la falsa sensación de haber guardado algo real.
 *
 *   - No llama a ningún endpoint, ni de lectura ni de escritura.
 *   - No toca reservas, incidencias ni bloqueos reales: las mutaciones de la
 *     demo V1 siguen viviendo en `pmsRuntimeStore`. Aquí solo se guarda lo
 *     que es propio de esta vista previa (estado de limpieza, peticiones del
 *     huésped, check-in simulado y el rol que se está previsualizando).
 *   - Las peticiones del huésped se guardan AQUÍ para todas las pantallas V2,
 *     no solo para Hoy: la ficha de reserva lee y escribe los mismos
 *     registros, así que un desayuno añadido desde una reserva aparece
 *     también en Hoy.
 *
 * Cada mutación incrementa `tick` para que la pantalla pueda mostrar el aviso
 * discreto "Actualizado en la vista previa" sin temporizadores repartidos.
 */
import { useSyncExternalStore } from "react";
import {
  HOUSEKEEPING_SEED, HOUSEKEEPING_FLOW, GUEST_REQUESTS, REQUEST_FLOW,
  DEFAULT_ROLE_PREVIEW, ROLE_PREVIEW_BY_KEY,
} from "@/data/todayV2Demo";

function seed() {
  return {
    rolePreview: DEFAULT_ROLE_PREVIEW,
    housekeeping: Object.fromEntries(
      Object.entries(HOUSEKEEPING_SEED).map(([unitId, v]) => [unitId, { ...v }]),
    ),
    requests: GUEST_REQUESTS.map((r) => ({ ...r, origin: "seed" })),
    arrivals: {},
    tick: 0,
  };
}

let state = seed();
const listeners = new Set();

function emit(next) {
  state = next;
  for (const fn of listeners) fn();
}

/** Toda mutación pasa por aquí, así el aviso de "actualizado" es automático. */
function commit(patch) {
  emit({ ...state, ...patch, tick: state.tick + 1 });
}

function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// ---------------------------------------------------------------------------
// Lecturas
// ---------------------------------------------------------------------------

export function getTodayV2() {
  return state;
}

export function useTodayV2() {
  return useSyncExternalStore(subscribe, getTodayV2);
}

/** Solo para tests: vuelve al dataset semilla. */
export function resetTodayV2() {
  emit(seed());
}

// ---------------------------------------------------------------------------
// Mutaciones (todas locales a la sesión)
// ---------------------------------------------------------------------------

/** Rol que se está previsualizando. No cambia permisos ni toca `auth`. */
export function setRolePreview(roleKey) {
  if (!ROLE_PREVIEW_BY_KEY[roleKey] || roleKey === state.rolePreview) return;
  commit({ rolePreview: roleKey });
}

function nextIn(flow, current) {
  const i = flow.indexOf(current);
  return i >= 0 && i < flow.length - 1 ? flow[i + 1] : current;
}

/** Avanza una limpieza al siguiente estado del ciclo (§8.1). */
export function advanceHousekeeping(unitId) {
  const current = state.housekeeping[unitId];
  if (!current) return;
  const status = nextIn(HOUSEKEEPING_FLOW, current.status);
  if (status === current.status) return;
  commit({
    housekeeping: { ...state.housekeeping, [unitId]: { ...current, status } },
  });
}

/** Asigna la limpieza de una unidad a alguien del equipo de la demo. */
export function assignHousekeeping(unitId, staffId) {
  const current = state.housekeeping[unitId];
  if (!current || !staffId || current.assignee === staffId) return;
  const status = current.status === "PENDING" && staffId ? "ASSIGNED" : current.status;
  commit({
    housekeeping: { ...state.housekeeping, [unitId]: { ...current, assignee: staffId, status } },
  });
}

/** Avanza una petición del huésped al siguiente estado (§9.2). */
export function advanceRequest(requestId) {
  let touched = false;
  const requests = state.requests.map((r) => {
    if (r.request_id !== requestId) return r;
    const status = nextIn(REQUEST_FLOW, r.status);
    if (status === r.status) return r;
    touched = true;
    return { ...r, status };
  });
  if (touched) commit({ requests });
}

/** Marca una petición como entregada de un tirón. */
export function completeRequest(requestId) {
  let touched = false;
  const requests = state.requests.map((r) => {
    if (r.request_id !== requestId || r.status === "delivered") return r;
    touched = true;
    return { ...r, status: "delivered" };
  });
  if (touched) commit({ requests });
}

let serviceCounter = 0;

/**
 * Alta de un pedido de servicio desde la ficha de reserva. Referencia un
 * servicio del catálogo de Ajustes (`service_id`), así que el precio y el
 * nombre salen de allí y no se copian aquí.
 */
export function addServiceOrder(payload) {
  serviceCounter += 1;
  const request = {
    request_id: `GR-SESSION-${String(serviceCounter).padStart(3, "0")}`,
    type: "service",
    service_id: payload.service_id,
    reservation_id: payload.reservation_id,
    villa_id: payload.villa_id || null,
    requested_at: payload.requested_at || "ahora",
    scheduled_for: payload.scheduled_for || null,
    quantity: Number(payload.quantity || 1),
    status: payload.status || "requested",
    assignee: null,
    origin: "created_in_session",
  };
  commit({ requests: [...state.requests, request] });
  return request;
}

/** Retira un pedido de servicio de la vista previa. */
export function removeRequest(requestId) {
  if (!state.requests.some((r) => r.request_id === requestId)) return;
  commit({ requests: state.requests.filter((r) => r.request_id !== requestId) });
}

/**
 * Check-in simulado de una llegada. No escribe en la reserva: el
 * `stay_status` real de la demo V1 sigue siendo suyo, aquí solo se recuerda
 * que en esta vista previa ya se ha pulsado.
 */
export function markArrivalCheckedIn(reservationId) {
  if (state.arrivals[reservationId]?.checked_in) return;
  commit({
    arrivals: { ...state.arrivals, [reservationId]: { checked_in: true } },
  });
}
